/* eslint-disable no-self-compare */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-bitwise */
import {
  UserOutlined,
  PhoneOutlined,
  HomeOutlined,
  IdcardOutlined,
  TeamOutlined,
  TagOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  ExclamationCircleFilled,
  BranchesOutlined,
  CarryOutOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import {
  Button, Modal, Skeleton, Result, Image, Tag, Tooltip
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import useFetchData from '../../hooks/useFetchData';
import { reFetchData } from '../../store/slice/appSlice';
import ApiService from '../../utils/apiService';
import notificationWithIcon from '../../utils/notification';
import ProfileEditModal from '../shared/ProfileEditModal';
import { isXrole } from '../../utils/authentication';
import { formatDate, getCardTypeResponse, getRoleResponse } from '../../utils/format';

const { confirm } = Modal;

// Mapping icônes
const iconMap = {
  Quartier: <HomeOutlined />,
  Téléphone: <PhoneOutlined />,
  Rôle: <TeamOutlined />,
  Matricule: <IdcardOutlined />,
  'Saisie par': <UserAddOutlined />,
  Taille: <TagOutlined />,
  'Type de carte': <CreditCardOutlined />,
  Carte: <CreditCardOutlined />,
  Référent: <UserOutlined />,
  Branch: <BranchesOutlined />,
  BirthDate: <CalendarOutlined />,
  'Date d’inscription': <CarryOutOutlined />
};

// Cookies Jotform
const jotformCookiesByUser = {
  andersenjessy500: 'JF_SESSION_USERNAME=andersenjessy500; JOTFORM_SESSION=581615e8-0946-2182-bc5a-cde8f693',
  andersenjessy: 'JF_SESSION_USERNAME=andersenjessy; JOTFORM_SESSION=a04eeee9-f7c4-5e66-51c6-072986ef'
};

const convertGoogleDriveUrl = (url) => {
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (match && match[1]) {
    return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1000`;
  }
  return url;
};

const getJotformCookie = (url) => {
  if (!url.includes('jotform.com')) return null;
  return url.includes('andersenjessy500') ?
    jotformCookiesByUser.andersenjessy500 :
    jotformCookiesByUser.andersenjessy || null;
};

function UserProfile({ id }) {
  const dispatch = useDispatch();
  const [loading, error, response] = useFetchData(`/user/${id}`);
  const [editProfileModal, setEditProfileModal] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [reloading, setReloading] = useState(false); // état bouton reload

  const user = response?.data;
  const firstLetter = user?.firstname ? user.firstname.charAt(0).toUpperCase() : '?';

  // logique réutilisable
  const loadImage = async () => {
    if (!user?.image) return setImageUrl(null);
    try {
      setReloading(true);
      const url = user.image;

      // Jotform
      const cookie = getJotformCookie(url);
      if (cookie) {
        const res = await axios.get(url, { responseType: 'blob', headers: { Cookie: cookie } });
        const blobUrl = URL.createObjectURL(res.data);
        setImageUrl(blobUrl);
        return;
      }

      // Google Drive
      if (url.includes('drive.google.com')) {
        setImageUrl(convertGoogleDriveUrl(url));
        return;
      }

      // Sinon URL directe
      setImageUrl(url);
    } catch (err) {
      console.error('Erreur chargement image :', err);
      setImageUrl(null);
    } finally {
      setReloading(false);
    }
  };

  useEffect(() => {
    loadImage();
  }, [user?.image, response]);

  const deleteUser = () => {
    confirm({
      title: 'Supprimer l’utilisateur',
      icon: <ExclamationCircleFilled />,
      content: 'Êtes-vous sûr de vouloir supprimer cet utilisateur ?',
      onOk() {
        return ApiService.delete(`/user/${id}`)
          .then((res) => {
            if (res?.result_code === 0) {
              notificationWithIcon('success', 'Succès', res?.result?.message || 'Utilisateur supprimé avec succès');
              dispatch(reFetchData());
            } else {
              notificationWithIcon('error', 'Erreur', 'Désolé, une erreur est survenue.');
            }
          })
          .catch(() => notificationWithIcon('error', 'Erreur', 'Oups, une erreur est survenue !'));
      }
    });
  };

  const infos = [
    { label: 'Quartier', value: user?.quartier },
    { label: 'Téléphone', value: user?.phone },
    { label: 'Rôle', value: <Tag color={getRoleResponse(user?.role).color}>{user?.role}</Tag> },
    { label: 'Référent', value: `${user?.referent?.civility || ''} ${user?.referent?.firstname || ''} ${user?.referent?.quartier || ''}` },
    { label: 'Matricule', value: <Tooltip title={user?.nfcCard}>{user?.matricule || '-'}</Tooltip> },
    { label: 'Branch', value: user?.branch },
    { label: 'Saisie par', value: user?.filledBy },
    { label: 'BirthDate', value: formatDate(user?.BirthDate) },
    { label: 'Taille', value: user?.size },
    { label: 'Carte', value: <Tag color={getCardTypeResponse(user?.cardType)?.color}>{getCardTypeResponse(user?.cardType)?.label || 'Carte'}</Tag> },
    { label: 'Date d’inscription', value: formatDate(user?.createdAt) }
  ].filter((i) => i.value);

  return (
    <div className='bg-white p-6 sm:p-8 border border-gray-200 rounded-lg w-full relative'>
      <Skeleton loading={loading} active>
        {error ? (
          <Result title='Erreur de chargement' subTitle={error} status='error' />
        ) : (
          <div className='flex flex-row gap-6 lg:gap-8'>

            {/* Image portrait responsive */}
            <div className='flex-shrink-0 w--[20%] sm:w-36 md:w-48 lg:w-64 h-[20%] sm:h-auto
                        sm:static absolute top-4 right-4 order-1 lg:order-1'
            >
              {imageUrl ? (
                <Image
                  width='100%'
                  height='100%'
                  src={imageUrl}
                  className='object-cover rounded-lg aspect-[3/4]'
                  alt={`${user?.firstname} picture` || 'User picture'}
                />
              ) : (
                <div className='w-full aspect-[3/4] bg-gray-200 rounded-lg flex flex-col items-center justify-center text-6xl font-bold text-gray-500'>
                  <span>{firstLetter}</span>
                  <Button
                    type='default'
                    size='small'
                    loading={reloading}
                    onClick={loadImage}
                    className='mt-2'
                  >
                    Recharger l’image
                  </Button>
                </div>
              )}
            </div>

            {/* Infos */}
            <div className='flex-1 flex flex-col justify-between order-2 lg:order-2'>
              <div>
                <h2 className='text-xl sm:text-2xl font-medium mb-4 sm:mb-6 truncate'>
                  {user?.civility ? `${user.civility} ` : ''}
                  {user?.firstname || 'Utilisateur'}
                </h2>

                <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-1 sm:gap-4'>
                  {infos.map((info, idx) => (
                    <div key={idx} className='flex items-center space-x-3'>
                      <span className='text-lg bg-gray-200 rounded px-2 py-1 flex-shrink-0'>{iconMap[info.label]}</span>
                      <span className='text-gray-700 font-medium truncate'>{info.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Boutons */}
              <div className='mt-4 flex flex-wrap justify-end gap-2'>
                <Button onClick={() => setEditProfileModal(true)} type='default'>
                  Modifier
                </Button>
                {isXrole('ADMIN_ROLE') && (
                <Button onClick={deleteUser} type='default' danger>
                  Supprimer
                </Button>
                )}
              </div>
            </div>

          </div>
        )}
      </Skeleton>

      {editProfileModal && (
      <ProfileEditModal
        editProfileModal={editProfileModal}
        setEditProfileModal={setEditProfileModal}
        user={user}
      />
      )}
    </div>
  );
}

export default React.memo(UserProfile);
